import { PermitTransactionV2OrchestrationsResultDTO } from '@ketch-com/figurehead'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { useMemo } from 'react'

// Legal bases where the default value of "allowed" is false
export const defaultOptOutLegalBases = ['not_applicable', 'consent_optin']

export type ConsentTabAggregateData = {
  consentRate: {
    optedInPurposes: string[]
    optedOutPurposes: string[]
  }
  adherenceRate: {
    defaultOverridenPurposes: string[]
    defaultNotOverridenPurposes: string[]
  }
  identityMatchRate: {
    missingIdentityConnections: string[]
    successfulConnections: string[]
  }
  totalPurposesCount: number
  totalConnectionsCount: number
}

export const useConsentTabAggregateData = () => {
  const { consentData, consentTransactionData } = useAuditLogContext()

  const aggregateData: ConsentTabAggregateData = useMemo(() => {
    const result: ConsentTabAggregateData = {
      consentRate: {
        optedInPurposes: [],
        optedOutPurposes: [],
      },
      adherenceRate: {
        defaultOverridenPurposes: [],
        defaultNotOverridenPurposes: [],
      },
      identityMatchRate: {
        missingIdentityConnections: [],
        successfulConnections: [],
      },
      totalPurposesCount: 0,
      totalConnectionsCount: 0,
    }

    // Calculate counts
    consentData.forEach(purpose => {
      // Skip if no purpose code
      if (!purpose.code) return

      // Add to opted in or opted out purposes
      if (purpose.allowed) {
        result.consentRate.optedInPurposes.push(purpose.code)
      } else {
        result.consentRate.optedOutPurposes.push(purpose.code)
      }

      // Add to default overridden or not overridden purposes
      const defaultFalseOverridenToTrue =
        purpose.legalBasis && defaultOptOutLegalBases.includes(purpose.legalBasis) && purpose.allowed
      const defaultTrueOverridenToFalse =
        purpose.legalBasis && !defaultOptOutLegalBases.includes(purpose.legalBasis) && !purpose.allowed
      if (defaultFalseOverridenToTrue || defaultTrueOverridenToFalse) {
        result.adherenceRate.defaultOverridenPurposes.push(purpose.code)
      } else {
        result.adherenceRate.defaultNotOverridenPurposes.push(purpose.code)
      }

      // Increment total purposes counter
      result.totalPurposesCount++
    })

    consentTransactionData.forEach(transaction => {
      Object.values(transaction.orchestrations || {}).forEach(connection => {
        // Skip if no connection code
        if (!connection.connectionCode) return

        // Add successful connections
        if (
          connection.result ===
          PermitTransactionV2OrchestrationsResultDTO.OrchestratedPermitTransactionV2OrchestrationsResult
        ) {
          result.identityMatchRate.successfulConnections.push(connection.connectionCode)
        }

        // Add missing identity connections
        if (
          connection.result ===
          PermitTransactionV2OrchestrationsResultDTO.InsufficientIdentifiersPermitTransactionV2OrchestrationsResult
        ) {
          result.identityMatchRate.missingIdentityConnections.push(connection.connectionCode)
        }

        // Increment total connections counter
        result.totalConnectionsCount++
      })
    })

    return result
  }, [consentData, consentTransactionData])

  return { aggregateData }
}
